// src/feed/useVisitToFeedBridge.ts
//
// Observes the user's visits collection and writes a feed_item for
// each NEW verified visit. Idempotency:
//   • In-memory: a Set tracks visit ids we've processed in this
//     session.
//   • Across sessions: each visit doc gets a `feed_item_id` field
//     written by this bridge after a successful create. If that
//     field is already set, we skip — no duplicate feed entries
//     across page reloads.
//
// Why client-side (vs. a Cloud Function trigger): visits are saved
// from the streamTourGuide cloud function, but the user's visibility
// preference + the snapshot fields aren't all available there. Doing
// it client-side keeps the bridge logic next to the rest of the
// feed code, easier to evolve. Trade-off: feed item only appears
// while the client is open. Acceptable for v1; a server-side
// fan-out can come in phase 2 if needed.

import { useEffect, useRef } from 'react';
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { writeFeedItem } from './writeFeedItem';

export function useVisitToFeedBridge(): void {
  const processedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const visitsRef = collection(db, 'users', user.uid, 'visits');
    const q = query(visitsRef, orderBy('verified_at', 'desc'));

    const unsub = onSnapshot(
      q,
      async (snap) => {
        for (const change of snap.docChanges()) {
          // Only react to ADDED visits — modifications shouldn't
          // create new feed items, and removals shouldn't auto-
          // delete feed history (deliberate; a user removing a
          // visit doesn't necessarily want their feed post gone).
          if (change.type !== 'added') continue;
          const id = change.doc.id;
          if (processedRef.current.has(id)) continue;
          processedRef.current.add(id);

          const data = change.doc.data() as any;

          // Cross-session idempotency stamp. If set, we already
          // bridged this visit on a previous session — skip.
          if (data.feed_item_id) continue;

          // Don't backfill ancient visits — only bridge ones from
          // the last 24 hours. Backfilling weeks of history all at
          // once would flood the feed and probably surprise the
          // user.
          const verifiedAt =
            data.verified_at && typeof data.verified_at.toDate === 'function'
              ? data.verified_at.toDate()
              : null;
          if (!verifiedAt) continue;
          const ageMs = Date.now() - verifiedAt.getTime();
          if (ageMs > 24 * 60 * 60 * 1000) continue;

          // DEFENCE-IN-DEPTH dedup: query feed_items for an
          // existing entry whose source.id matches this visit. The
          // primary idempotency mechanism is the feed_item_id stamp
          // on the visit doc, but that stamp can be missed when:
          //
          //   • The first session created the feed_item but the
          //     stamp write failed (network error, transient
          //     permission glitch). Next session sees no stamp and
          //     would create a second feed_item.
          //   • Two clients (rare but possible) race on the same
          //     fresh visit.
          //
          // Querying first eliminates both cases at the cost of one
          // read per visit. The query uses two single-field equality
          // filters (source.kind, source.id) which Firestore serves
          // from auto-indexes — no composite index needed.
          //
          // If we find an existing one, we ALSO repair the missing
          // stamp on the visit so the next session can short-circuit
          // without this read.
          try {
            const existingQ = query(
              collection(db, 'feed_items'),
              where('source.kind', '==', 'visit'),
              where('source.id', '==', id),
              limit(1)
            );
            const existingSnap = await getDocs(existingQ);
            if (!existingSnap.empty) {
              const existingId = existingSnap.docs[0].id;
              try {
                await updateDoc(
                  doc(db, 'users', user.uid, 'visits', id),
                  { feed_item_id: existingId }
                );
              } catch {
                // Repair is best-effort; if it fails we'll just
                // re-discover the existing item next session.
              }
              continue;
            }
          } catch (err) {
            // Query failed — fall through to the original create
            // path. Worst case we get a duplicate in this rare
            // failure mode; better than silently dropping the post.
            console.warn(
              'useVisitToFeedBridge: existing-item query failed, proceeding with create:',
              err
            );
          }

          const feedItemId = await writeFeedItem({
            kind: 'postcard',
            snapshot: {
              title: data.landmark_name || 'Visited place',
              caption: data.reasoning || undefined,
              location:
                data.location?.latitude && data.location?.longitude
                  ? {
                      lat: data.location.latitude,
                      lng: data.location.longitude,
                      place_name: data.landmark_name,
                    }
                  : undefined,
              image_path: data.photo_reference || undefined,
              preview_url: data.photo_reference || undefined,
            },
            source: { kind: 'visit', id },
          });

          if (feedItemId) {
            try {
              await updateDoc(doc(db, 'users', user.uid, 'visits', id), {
                feed_item_id: feedItemId,
              });
            } catch (err) {
              // Non-fatal — duplicate-prevention falls back to the
              // existing-item query above on subsequent loads.
              console.warn(
                'useVisitToFeedBridge: feed_item_id write failed:',
                err
              );
            }
          }
        }
      },
      (err) => {
        console.warn('useVisitToFeedBridge listener error:', err);
      }
    );

    return () => unsub();
  }, []);
}
